/* eslint-disable react-hooks/exhaustive-deps */
import {KlassappTableHOC} from "HOC"
import styles from "./ArchivedUsers.module.css"
import {useModel, useVisible} from "hooks"
import {useEffect, useState} from "react"
import {checkPermission, handleError} from "helpers"
import {BaseNewFilter, ConfirmPopup, KlassappTable, KlassappTableHeader} from "uiKit"
import {Col, Row, Tooltip} from "antd"
import {Icon} from "components"
import {FormLabel} from "components/Form"
import {archivedUsersService} from "services"
import {KlassDropdown} from "components/Select"
import {Auth} from "types/auth"
import {BaseRangePicker} from "components/DateTimePicker"
import moment from "moment"
import {KlassappTableProps} from "types/common"
import {Permissions} from "@edular/permissions"
import {useTranslation} from "react-i18next"

const USER_TYPE_OPTIONS = [
    {id: Auth.UserProfileType.Staff, name: "Staff"},
    {id: Auth.UserProfileType.Student, name: "Student"},
    {id: Auth.UserProfileType.Others, name: "Others"}
]

const INITIAL_FILTERS = {
    userTypes: [],
    dateRange: null
}

type Props = KlassappTableProps

export function ArchivedUsersPage({
    data,
    page,
    total,
    pageSize,
    columns,
    fields,
    allFields,
    isLoading,
    menuActions,
    isHideMenuActions,
    dispatch,
    dispatchFunc,
    onChangePage,
    onChangeRowPerPage,
    onChangeFields,
    onDraggableColumn,
    onChangeAllFields,
    onUpdateRowData,
    onUpdateTableData
}: Props) {
    const model = useModel()
    const {t} = useTranslation(["common"])
    const [filter, setFilter] = useState(INITIAL_FILTERS)
    const [search, setSearch] = useState("")
    const [restoreItem, setRestoreItem] = useState<any>(null)
    const restoreConfirmPopup = useVisible()

    const canRestore = checkPermission({staff: [Permissions.Staff.Users.Archived.Edit]}, model)

    useEffect(() => {
        dispatch({isClassComponent: false, keyRender: "profileId"})
        dispatchFunc([
            {key: "getListData", func: getData},
            {key: "getFields", func: getFields},
            {key: "getColumns", func: getColumns},
            {key: "getMenuActions", func: getMenuActions}
        ])
    }, [])

    useEffect(() => {
        getData()
    }, [search, page, pageSize])

    const getFilterParams = () => {
        const filterParams: any = {search}
        if (filter.userTypes?.length) {
            filterParams.userTypes = filter.userTypes.map((x) => x.id)
        }
        if (filter.dateRange?.length) {
            filterParams.dateRange = filter.dateRange.map((x) => x.format("YYYY-MM-DD"))
        }
        return filterParams
    }

    async function getData() {
        try {
            dispatch({isLoading: true})
            const filterParams = getFilterParams()

            const {data: listData, total: totalCount} = await archivedUsersService.list({
                range: {page, pageSize},
                filter: filterParams
            })

            const formattedData = listData.map((x) => ({
                ...x,
                id: x.profileId,
                userTypeHtml: USER_TYPE_OPTIONS.find((y) => y.id === x.type)?.name,
                statusHtml: renderStatusHtml(x)
            }))

            dispatch({data: formattedData, total: totalCount})
        } catch (error) {
            handleError(error)
        } finally {
            dispatch({isLoading: false})
        }
    }

    function getColumns() {
        return [
            {
                title: "Archived date",
                field: "archivedAt",
                fieldType: "date",
                headerStyle: {minWidth: "120px"}
            },
            {
                title: "Name",
                field: "fullName",
                headerStyle: {minWidth: "150px"}
            },
            {
                title: "User's Type",
                field: "userTypeHtml",
                headerStyle: {minWidth: "100px"}
            },
            {
                title: "Status",
                field: "statusHtml",
                headerStyle: {minWidth: "80px"}
            },
            {
                title: "Custom profile ID",
                field: "customProfileId",
                headerStyle: {minWidth: "140px"}
            }
        ]
    }

    function getFields() {
        return ["Archived date", "Name", "User's Type", "Status", "Custom profile ID"]
    }

    const renderStatusHtml = (item) => {
        return (
            <div className={styles.summaryWrap}>
                {item.isProfileArchived && (
                    <Tooltip title="Profile archived">
                        <span className={styles.statusIcon}>
                            <Icon icon="PROFILE_FILL" color="var(--warning-400-base)" />
                        </span>
                    </Tooltip>
                )}
                {item.isUserArchived && (
                    <Tooltip title="User archived">
                        <span className={styles.statusIcon}>
                            <Icon icon="USER" color="var(--black-100)" />
                        </span>
                    </Tooltip>
                )}
            </div>
        )
    }

    const onClickRestore = (row) => {
        setRestoreItem(row)
        restoreConfirmPopup.open()
    }

    const handleRestore = async () => {
        if (!restoreItem) return
        restoreConfirmPopup.close()
        try {
            dispatch({isLoading: true})
            await archivedUsersService.restore(restoreItem.profileId)
            setRestoreItem(null)
            await getData()
        } catch (error) {
            handleError(error)
        } finally {
            dispatch({isLoading: false})
        }
    }

    const getMenuActions = () => {
        if (!canRestore) {
            return []
        }
        return [
            {
                title: t("common:action.restore"),
                icon: "REPLY",
                action: onClickRestore
            }
        ]
    }

    const onClickRowItem = () => {}

    const onClickClearFilter = () => {
        setFilter(INITIAL_FILTERS)
    }

    const onChangeFilter = (key, value) => {
        const newFilter = {...filter}
        newFilter[key] = value
        setFilter(newFilter)
    }

    const onSearchInput = (searchValue) => {
        setSearch(searchValue)
    }

    const onApplyFilter = () => {
        getData()
    }

    return (
        <div>
            <BaseNewFilter
                filter={filter}
                onClick={onApplyFilter}
                onClickClear={onClickClearFilter}
                onSearchInput={onSearchInput}>
                <Row gutter={[12, 12]}>
                    <Col span={12}>
                        <FormLabel label="Date Range" />
                        <BaseRangePicker
                            placeholder={["Date From", "To"]}
                            value={filter.dateRange ? [moment(filter.dateRange[0]), moment(filter.dateRange[1])] : null}
                            onChange={(value) => {
                                if (value) {
                                    onChangeFilter("dateRange", [moment(value[0]), moment(value[1])])
                                } else {
                                    onChangeFilter("dateRange", null)
                                }
                            }}
                        />
                    </Col>
                    <Col span={12}>
                        <FormLabel label="User's Type" />
                        <KlassDropdown
                            value={filter.userTypes}
                            placeholder="Select"
                            onChange={(option: any) => onChangeFilter("userTypes", option)}
                            options={USER_TYPE_OPTIONS}
                            isMulti
                        />
                    </Col>
                </Row>
            </BaseNewFilter>
            <Row className={styles.marginTop}>
                <Col span={24}>
                    <KlassappTableHeader
                        page={page}
                        total={total}
                        defaultPageSize={pageSize}
                        onChangePage={onChangePage}
                        onChangeRowPerPage={onChangeRowPerPage}
                        fields={fields}
                        allFields={allFields}
                        onChangeFields={onChangeFields}
                        onChangeAllFields={onChangeAllFields}
                        onDraggableColumn={onDraggableColumn}
                    />
                    <KlassappTable
                        columns={columns}
                        data={data}
                        isLoading={isLoading}
                        fields={fields}
                        allFields={allFields}
                        menuActions={isHideMenuActions || !canRestore ? [] : menuActions}
                        onChangeFields={onChangeFields}
                        onUpdateRowData={onUpdateRowData}
                        onUpdateTableData={onUpdateTableData}
                        onClickRowItem={onClickRowItem}
                        onChangeAllFields={onChangeAllFields}
                    />
                </Col>
            </Row>
            <ConfirmPopup
                isVisible={restoreConfirmPopup.isVisible}
                title={t("common:action.restore")}
                onClose={() => {
                    restoreConfirmPopup.close()
                    setRestoreItem(null)
                }}
                onConfirm={handleRestore}
                loading={isLoading}
                description={`Are you sure you want to restore ${restoreItem?.fullName ?? "this user"}?`}
                icon="REPLY"
            />
        </div>
    )
}

export const ArchivedUsers = KlassappTableHOC(ArchivedUsersPage)
